import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SendEmailDialog, type SendEmailAttachment } from "@/components/email/SendEmailDialog";
import { JobFilesAttachmentPicker } from "./JobFilesAttachmentPicker";
import { jobsApi, type SendJobEmailRequest } from "@/lib/jobs";
import { jobFiles } from "@/lib/job-files";
import { toast } from "@/hooks/use-toast";

export interface JobSendEmailDialogProps {
  jobId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTo?: string;
  defaultSubject?: string;
}

export function JobSendEmailDialog({
  jobId,
  open,
  onOpenChange,
  defaultTo = "",
  defaultSubject = "",
}: JobSendEmailDialogProps) {
  const qc = useQueryClient();
  const [flag, setFlag] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerResolveRef = useRef<((atts: SendEmailAttachment[]) => void) | null>(null);

  const sendMut = useMutation({
    mutationFn: (body: SendJobEmailRequest) => jobsApi.sendEmail(jobId, body),
    onSuccess: () => {
      toast.success("Email sent");
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      void qc.invalidateQueries({ queryKey: ["jobs", jobId, "emails"] });
      setFlag(false);
    },
    onError: (err: Error) => toast.error(`Send failed: ${err.message}`),
  });

  return (
    <>
      <SendEmailDialog
        open={open}
        onOpenChange={onOpenChange}
        defaultTo={defaultTo}
        defaultSubject={defaultSubject}
        defaultReplyToType="CurrentUser"
        extraHeaderSlot={
          <div className="flex items-center gap-2">
            <Checkbox
              id="job-flag-waiting"
              checked={flag}
              onCheckedChange={(v) => setFlag(v === true)}
            />
            <Label htmlFor="job-flag-waiting" className="text-xs whitespace-nowrap">
              Flag Job — Waiting on Client
            </Label>
          </div>
        }
        onUploadFromComputer={async (file) => {
          const dto = await jobFiles.upload(jobId, file);
          return {
            id: String(dto.jobFileId),
            name: file.name,
            source: "computer" as const,
          };
        }}
        onPickFromLibrary={() =>
          new Promise<SendEmailAttachment[]>((resolve) => {
            pickerResolveRef.current = resolve;
            setPickerOpen(true);
          })
        }
        onSend={async (payload) => {
          await sendMut.mutateAsync({
            toEmailAddress: payload.to,
            cc: payload.cc ?? null,
            bcc: payload.bcc ?? null,
            subject: payload.subject,
            htmlBody: payload.htmlBody,
            replyToType: payload.replyToType,
            attachmentJobFileIds: payload.attachmentIds
              .map((s) => Number(s))
              .filter((n) => Number.isFinite(n)),
            flagWaitingOnClient: flag,
          });
        }}
      />
      <JobFilesAttachmentPicker
        open={pickerOpen}
        jobId={jobId}
        onSelected={(atts) => {
          pickerResolveRef.current?.(atts);
          pickerResolveRef.current = null;
          setPickerOpen(false);
        }}
        onCancel={() => {
          pickerResolveRef.current?.([]);
          pickerResolveRef.current = null;
          setPickerOpen(false);
        }}
      />
    </>
  );
}
